import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { formatColumnHeader } from '../helpers/chartHelpers';

type Props = {
  data: any[];
  onScrollEnd?: () => void;
  hideColumnsByIndex?: string[]; // Array of column indices to hide (e.g., ['0', '2'] to hide first and third columns)
  hideSortingOnColumns?: string[]; // Array of column names to disable sorting for (e.g., ['activeUsers', 'inactiveUsers'])
  onSortChange?: (columnId: string, desc: boolean) => void;
  onRowSelect?: (props: any) => void;
  isFullHeight?: boolean;
  ragColoringColumns?: number[];
};

const TableGraph: React.FC<Props> = ({ data, hideColumnsByIndex, hideSortingOnColumns, onScrollEnd, onSortChange, onRowSelect, isFullHeight = false, ragColoringColumns }) => {
  // Keep API column names for sortBy; format only for display labels.
  const headers: string[] = data[0] || [];
  const rows = data.slice(1) || [];
  
  // State to track current sort
  const [currentSort, setCurrentSort] = React.useState<{
    columnId: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  const columns = useMemo<ColumnDef<any>[]>(
    () =>
      headers.map((header: string, index: number) => ({
        accessorKey: String(index),
        header: formatColumnHeader(header),
        cell: (info: any) => {
          const val = info.getValue();
          if (
            typeof header === 'string' &&
            header.toLowerCase().includes('compliance %') &&
            val !== null &&
            val !== undefined &&
            val !== '' &&
            !isNaN(Number(val))
          ) {
            return Math.floor(Number(val));
          }
          return val;
        },
      })),
    [data]
  );

  const tableData = useMemo(() => {
    return rows.map((row) =>
      row.reduce((acc: any, val: any, i: number) => {
        acc[i] = val;
        return acc;
      }, {})
    );
  }, [data]);

  // Create column visibility state from hideColumnsByIndex prop
  const columnVisibility = useMemo(() => {
    if (!hideColumnsByIndex || hideColumnsByIndex.length === 0) return {};
    
    return hideColumnsByIndex.reduce((acc, columnIndex) => {
      acc[columnIndex] = false;
      return acc;
    }, {} as Record<string, boolean>);
  }, [hideColumnsByIndex]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
    const threshold = 5; // Allow 5px tolerance for floating point precision
    const bottom = scrollHeight - scrollTop <= clientHeight + threshold;
    
    if (bottom) {
      onScrollEnd?.();
    }
  };

  const handleRowClick = (row: any, rowIndex: number) => {
    if (onRowSelect) {
      onRowSelect({
        row,
        rowIndex,
        chart: {
          getSelection: () => [{ row: rowIndex }]
        }
      });
    }
  };

  const handleSortChange = (columnId: string, desc: boolean) => {
    // Map column index to API column name (not the display-formatted header)
    const columnIndex = parseInt(columnId);
    const originalColumnName = headers[columnIndex];
    
    if (originalColumnName && onSortChange) {
      // Update current sort state
      const newDirection = desc ? 'desc' : 'asc';
      setCurrentSort({
        columnId: originalColumnName,
        direction: newDirection
      });
      
      onSortChange(originalColumnName, desc);
    }
  };

  // Helper function to get sort icon
  const getSortIcon = (columnName: string) => {
    if (currentSort && currentSort.columnId === columnName) {
      return currentSort.direction === 'asc' ? '↑' : '↓';
    }
    return null;
  };

  // Function to clear sorting
  const clearSort = () => {
    setCurrentSort(null);
    // You can optionally call onSortChange with null values to reset server-side sorting
    if (onSortChange) {
      onSortChange('', false);
    }
  };

  const isNumber = (value: any) => {
    if (typeof value === 'number') return !Number.isNaN(value);
    if (typeof value !== 'string') return false;
    const cleaned = value.replace(/,/g, '').replace(/%/g, '').trim();
    return cleaned !== '' && !Number.isNaN(Number(cleaned));
  };

  const getRAGColor = (value: any): { textColor: string; backgroundColor: string } | null => {
    if (!ragColoringColumns || ragColoringColumns.length === 0) {
      return null;
    }

    let percentage: number;
    if (typeof value === 'string') {
      const cleanedValue = value.replace('%', '').replace(',', '').replace(' ', '').trim();
      percentage = parseFloat(cleanedValue);
    } else if (typeof value === 'number') {
      percentage = value;
    } else {
      return null;
    }

    if (isNaN(percentage)) {
      return null;
    }

    let textColor: string;
    let backgroundColor: string;

    if (percentage < 74) {
      textColor = '#EF4444';
      backgroundColor = 'rgba(239, 68, 68, 0.2)';
    } else if (percentage >= 75 && percentage < 90) {
      textColor = '#EAB000';
      backgroundColor = 'rgba(234, 176, 0, 0.2)';
    } else if (percentage >= 90) {
      textColor = '#22C55E';
      backgroundColor = 'rgba(34, 197, 94, 0.2)';
    } else {
      textColor = '#EF4444';
      backgroundColor = 'rgba(239, 68, 68, 0.2)';
    }

    return { textColor, backgroundColor };
  };

  const shouldApplyRAGColoring = (columnIndex: number): boolean => {
    return ragColoringColumns ? ragColoringColumns.includes(columnIndex) : false;
  };

  // Memoized map of numeric columns - computed once, used everywhere
  // Simple and efficient: check only the first data row (index 0) for each column
  const numericColumnsMap = useMemo<Record<number, boolean>>(() => {
    const map: Record<number, boolean> = {};
    
    if (rows.length === 0) return map;
    
    headers.forEach((_: string, columnIndex: number) => {
      const firstValue = rows[0]?.[columnIndex];
      if (firstValue !== undefined && firstValue !== null && firstValue !== '') {
        map[columnIndex] = isNumber(firstValue);
      } else {
        map[columnIndex] = false;
      }
    });
    
    return map;
  }, [headers, rows]);

  // Stable % widths so sort icons / reordered rows don't reshuffle columns.
  const columnWidths = useMemo<Record<number, React.CSSProperties>>(() => {
    const widths: Record<number, React.CSSProperties> = {};
    const visibleIndexes: number[] = headers
      .map((_: string, index: number) => index)
      .filter((index: number) => columnVisibility[String(index)] !== false);

    const hashIndexes = visibleIndexes.filter((index: number) => headers[index] === '#');
    const numericIndexes = visibleIndexes.filter(
      (index: number) => headers[index] !== '#' && numericColumnsMap[index]
    );
    const textIndexes = visibleIndexes.filter(
      (index: number) => headers[index] !== '#' && !numericColumnsMap[index]
    );

    hashIndexes.forEach((index: number) => {
      widths[index] = { width: '80px', minWidth: '80px', maxWidth: '80px' };
    });

    if (visibleIndexes.length === 0) return widths;

    if (textIndexes.length === 0) {
      const equalPct = `${(100 / visibleIndexes.length).toFixed(2)}%`;
      visibleIndexes.forEach((index: number) => {
        if (!widths[index]) widths[index] = { width: equalPct, minWidth: '100px' };
      });
      return widths;
    }

    const totalWeight = numericIndexes.length * 1.0 + textIndexes.length * 1.4;
    const numericWidthPct = totalWeight > 0 ? (1.0 / totalWeight) * 100 : 0;
    const textWidthPct = totalWeight > 0 ? (1.4 / totalWeight) * 100 : 0;

    numericIndexes.forEach((index: number) => {
      widths[index] = {
        width: `${numericWidthPct.toFixed(2)}%`,
        minWidth: '110px',
      };
    });

    textIndexes.forEach((index: number) => {
      widths[index] = {
        width: `${textWidthPct.toFixed(2)}%`,
        minWidth: '150px',
      };
    });

    return widths;
  }, [headers, numericColumnsMap, columnVisibility]);

  const totalTableMinWidth = useMemo(() => {
    const visibleIndexes = headers
      .map((_: string, index: number) => index)
      .filter((index: number) => columnVisibility[String(index)] !== false);
    if (visibleIndexes.length <= 5) return 0;
    return visibleIndexes.reduce((sum, idx) => {
      const w = columnWidths[idx];
      const minW = parseInt(String(w?.minWidth || '110'), 10) || 110;
      return sum + minW;
    }, 0);
  }, [headers, columnVisibility, columnWidths]);

  const getColumnWidth = (columnIndex: number) => columnWidths[columnIndex] || {};

  return (
    <div
      className="table-graph-scroll-container"
      style={{
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: isFullHeight ? '100%' : 'calc(100% - 70px)',
        width: '100%',
      }}
      onScroll={onScroll}
    >
      <table
        className="w-full border-collapse text-sm"
        style={{
          borderSpacing: 0,
          tableLayout: 'fixed',
          width: '100%',
          minWidth: totalTableMinWidth > 0 ? `${totalTableMinWidth}px` : '100%',
        }}
      >
        <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="custom-header-google-tables" >
              {headerGroup.headers.map((header, headerIndex) => {
                // Get the actual column index from the header's accessorKey
                const actualColumnIndex = parseInt(header.column.id);
                const columnName = headers[actualColumnIndex];
                const isSortingDisabled = hideSortingOnColumns && hideSortingOnColumns.includes(columnName);
                const columnWidth = getColumnWidth(actualColumnIndex);
                
                return (
                  <th
                    key={header.id}
                    className="text-left"
                    style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      textAlign: 'left',
                      color: '#08305D',
                      minHeight: '40px',
                      height: 'auto',
                      padding: '8px 12px',
                      ...columnWidth,
                      borderBottom: '2px solid #ffffff',
                      borderRight: headerIndex < headerGroup.headers.length - 1 ? '1px solid #f0f0f0' : 'none',
                      borderLeft: 'none',
                      borderTop: 'none',
                      backgroundColor: '#ECECEF',
                      margin: 0,
                      cursor: isSortingDisabled ? 'default' : 'pointer',
                      boxSizing: 'border-box',
                      verticalAlign: 'middle',
                    }}
                    onClick={() => {
                      if (isSortingDisabled) return;
                      
                      const isCurrentlySorted = currentSort && currentSort.columnId === columnName;
                      const isCurrentlyDesc = currentSort && currentSort.direction === 'desc';
                      
                      if (isCurrentlySorted) {
                        if (isCurrentlyDesc) {
                          // Currently desc, clear sort
                          clearSort();
                        } else {
                          // Currently asc, change to desc
                          handleSortChange(header.column.id, true);
                        }
                      } else {
                        // Not currently sorted, set to asc
                        handleSortChange(header.column.id, false);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', minWidth: 0, width: '100%' }}>
                      <span
                        title={typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : undefined}
                        style={{
                          whiteSpace: 'normal',
                          wordBreak: 'normal',
                          overflowWrap: 'break-word',
                          lineHeight: 1.3,
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {!isSortingDisabled && (
                        <span
                          style={{
                            fontSize: '16px',
                            paddingBottom: '2px',
                            fontWeight: 'bold',
                            width: '14px',
                            minWidth: '14px',
                            flexShrink: 0,
                            textAlign: 'center',
                            visibility: getSortIcon(columnName) ? 'visible' : 'hidden',
                          }}
                        >
                          {getSortIcon(columnName) || '↑'}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, rowIndex) => (
            <tr 
              key={row.id} 
              className="custom-rows-google-tables cursor-pointer"
              style={{
                minHeight: '32px',
                height: 'auto',
                backgroundColor: 'transparent',
                border: 'none',
                margin: 0,
                padding: 0
              }}
              onClick={() => handleRowClick(row.original, rowIndex)}
            >
              {row.getVisibleCells().map((cell, cellIndex) => {
                const value = cell.getValue();
                
                const actualColumnIndex = parseInt(cell.column.id);
                const shouldApplyRAG = shouldApplyRAGColoring(actualColumnIndex);
                const ragColors = shouldApplyRAG ? getRAGColor(value) : null;
                
                const baseBackgroundColor = rowIndex % 2 === 0 ? 'white' : '#fafafa';
                const columnWidth = getColumnWidth(actualColumnIndex);
                
                return (
                  <td 
                    key={cell.id} 
                    title={value !== null && value !== undefined ? String(value) : undefined}
                    style={{
                      paddingTop: '6px',
                      paddingBottom: '6px',
                      paddingLeft: '12px',
                      paddingRight: '12px',
                      minHeight: '32px',
                      height: 'auto',
                      ...columnWidth,
                      borderRight: cellIndex < row.getVisibleCells().length - 1 ? '1px solid #f0f0f0' : 'none',
                      borderLeft: 'none',
                      borderTop: 'none',
                      borderBottom: '1px solid #f0f0f0',
                      margin: 0,
                      textAlign: 'left',
                      fontSize: '12px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: 1.3,
                      verticalAlign: 'middle',
                      color: ragColors?.textColor || undefined,
                      backgroundColor: ragColors?.backgroundColor || baseBackgroundColor,
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => {
                      if (shouldApplyRAG && ragColors) {
                        e.currentTarget.style.backgroundColor = ragColors.backgroundColor;
                      } else {
                        e.currentTarget.style.backgroundColor = '#f0f8ff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (shouldApplyRAG && ragColors) {
                        e.currentTarget.style.backgroundColor = ragColors.backgroundColor;
                      } else {
                        e.currentTarget.style.backgroundColor = baseBackgroundColor;
                      }
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TableGraph;
